/**
 * Tanjong Life · L9b · the before and after, and the room deciding whether to keep it.
 *
 * Scoped to `siu-triage-sentinel`. The agent analyses its own outcomes, proposes a change
 * to what it carries on a finding, and then STOPS at a person: Promote, or Hold it staged.
 * Holding is a real branch that mutates nothing, which is the only reason the offer means
 * anything.
 *
 * TWO RULES SHAPE WHAT THIS PATCH MAY TOUCH.
 * · R-TOK — `patchAgentDetail` is a plain shallow merge and does NOT resolve story tokens,
 *   so a slice restated here must be token-free in the boot asset. `goals` is (`'Today'`,
 *   `'today'`); `memory[].date` and `autonomy.decisions[].when` are NOT — they hold
 *   `§t-0|date` and `§t-240|ago`. Restating either would put a raw token on the Memory and
 *   Autonomy tabs, so the lesson is written where it can be written honestly: a fifth goal
 *   carrying the approver's name, and a second run of the suite that caught it.
 * · The shallow merge cannot ADD to a slice, only replace it — so `releases` belongs to
 *   exactly one beat, and that beat is L10 (`tanjong-apply-threshold`). If this one wrote a
 *   version too, applying the recommendation afterwards would silently delete it.
 *
 * The headline evaluation is deliberately untouched. 82 → 90 belongs to the Apply beat, and
 * an agent that moved its own evaluation by being asked about it is the thing this act
 * argues against.
 */
import type { AgentDetailContent } from '@wand/shared/dataset/agent_detail.js';
import type { ScenarioStep } from '../director.js';
import {
  SIU_BENCH_EVIDENCE,
  SIU_BENCH_SELFCORRECT,
  SIU_BENCH_THRESHOLD,
  SIU_BENCH_THRESHOLD_APPLIED,
  SIU_GOALS_V2,
} from './tanjong-siu-slices.js';

/**
 * A full slice — the shallow merge drops anything not restated — so the calibration suite is
 * restated as it stands: at 78 when the threshold recommendation was applied first (the fraud
 * story's order, L10 before L9b), else at 30. Writing 30 over an applied 78 lost the Apply.
 */
const promoted = (threshold: typeof SIU_BENCH_THRESHOLD): Partial<AgentDetailContent> => ({
  goals: {
    goals: SIU_GOALS_V2,
    benches: [{ ...SIU_BENCH_SELFCORRECT, when: 'just now' }, SIU_BENCH_EVIDENCE, threshold],
  },
});
const promote = (threshold: typeof SIU_BENCH_THRESHOLD): ScenarioStep => ({
  do: 'patchAgentDetail',
  pk: 'siu-triage-sentinel',
  patch: promoted(threshold),
  summary:
    'Panel tenure carried on every concentration finding · a fifth goal, written by the agent and approved by Amara Mensah',
  delay: 3200,
});

const RESULT_ICON = {
  sentinel: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M12 12 19 5',
  analyst: 'M12 3l8 3v6c0 5-8 9-8 9s-8-4-8-9V6l8-3z M9 12l2 2 4-4',
  external:
    'M12 2v2 M5 9h14a1 1 0 0 1 1 1v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a1 1 0 0 1 1-1z M9 14h.01 M15 14h.01 M12 4a1 1 0 1 0 0 2 1 1 0 0 0 0-2z',
  desk: 'M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M10 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
};

/**
 * The proof beat itself, held apart from any one scenario so BOTH doors reach the same
 * steps: the direct question (`tanjong-self-evolve`, keyword-routed) and the offer at the
 * end of the self-account (`tanjong-chat-sentinel`, behind an explicit ask). One beat, two
 * ways in, and the mutation still sits behind a person choosing Promote — which is the
 * reason the two scenarios were never merged into one steps array: a scenario cannot see
 * which of its keywords brought it here, so a merge would fire the promote gate on
 * "why not just use the fraud model?" too.
 */
export const SELF_EVOLVE_STEPS: ScenarioStep[] = [
  {
    do: 'thinking',
    steps: [
      {
        tool: {
          name: 'runs.read',
          input: 'own sweeps · 90 days · findings and what the desk did with them',
          output: '31 cases opened · 26 acted on · 5 closed with "the clinic is new to the panel"',
        },
      },
      {
        tool: {
          name: 'runs.read',
          input: 'provider concentration findings · grouped by panel tenure at the time of the rank',
          output:
            'clinics on the panel under 6 months: 5 findings, 5 closed without action · over 6 months: 21 findings, 21 acted on',
        },
      },
      {
        thought:
          'Every finding the desk dismissed had the same shape and I did not see it, because I was reading the concentration and not the tenure behind it. That is not a weight I am allowed to change — it is a question I should have been carrying.',
      },
    ],
    delay: 900,
  },
  {
    do: 'say',
    text: 'Here is the before and after on the same provider signature. Before: five claims through one clinic in ninety-six days, reported as a concentration finding at the desk manager. Five of those went back with the same sentence — the clinic joined the panel four months ago, so a cluster through it is the panel working. After: the same finding carries the panel tenure on its face, and where the tenure is under six months it says so in the first line rather than in an attachment. Twenty-one findings on established clinics are unaffected, because nothing about them was wrong.',
    sources: ['31 cases opened · 26 acted on', 'FRD-3309 · cleared at 81 · 20 August'],
    delay: 1300,
  },
  {
    do: 'insight',
    title: 'What it is asking for, and what it is not',
    body: 'It is asking to change what it CARRIES on a finding. It is not asking to change the indicator weight, the referral threshold or any of its five authorities — those belong to the Financial Crime desk and it has no field in which they could be granted. Approving this records a goal in its own words with your name on it. Holding it changes nothing at all, and the proposal stays on the page.',
    sources: ['Authorities · 5, every one 0 of 0 · Halt'],
    delay: 1400,
  },
  {
    do: 'ask',
    id: 'promote',
    prompt: 'Promote what it wrote, or hold it staged?',
    options: [
      { id: 'promote', label: 'Promote it' },
      { id: 'hold', label: 'Hold it staged' },
    ],
    delay: 900,
  },
  {
    do: 'branch',
    cases: {
      promote: [
        {
          // The Apply moves the headline to 90 (82 before it): that is how the record says it ran.
          do: 'checkRecord',
          when: [
            {
              check: { kind: 'evaluation', pk: 'siu-triage-sentinel', atLeast: 90 },
              steps: [promote(SIU_BENCH_THRESHOLD_APPLIED)],
            },
          ],
          else: [promote(SIU_BENCH_THRESHOLD)],
        },
        {
          do: 'patchUniversity',
          patch: {
            benchmarks: [
              {
                id: 'bu-tl-siu-selfcorrect',
                observed: 'Re-run just now. SIU Triage Sentinel scored 88, up from 71 on the first run.',
                results: [
                  {
                    agent: 'SIU Triage Sentinel',
                    icon: RESULT_ICON.sentinel,
                    accent: 'red',
                    score: 88,
                    prev: 71,
                    kind: 'agent',
                    self: true,
                  },
                  {
                    agent: 'Claims Fraud Analyst',
                    icon: RESULT_ICON.analyst,
                    accent: 'amber',
                    score: 63,
                    prev: 68,
                    kind: 'agent',
                  },
                  {
                    agent: 'Fraud Score Agent',
                    icon: RESULT_ICON.external,
                    accent: 'blue',
                    score: 49,
                    prev: 55,
                    kind: 'agent',
                  },
                  {
                    agent: 'The desk, unaided',
                    icon: RESULT_ICON.desk,
                    accent: 'brand',
                    score: 61,
                    prev: 63,
                    kind: 'human',
                  },
                ],
              },
            ],
          },
          summary: 'Self-correction suite re-run — 71 → 88, and the other three where they were',
          delay: 900,
        },
        {
          do: 'say',
          text: 'Done. There is a fifth goal on its Goals tab in its own words, and the line beneath it says who approved it, because a change an agent makes to itself with nobody named on it is not governance. The self-correction suite now has a second run against it: 88, up from 71. What did NOT move is the headline evaluation — it is still 82. A change to what an agent carries is not the same event as an evaluation of it, and nothing here let it mark its own work.',
          sources: ['Goals · 5', 'SIU Queue Self-Correction Suite · second run'],
          delay: 1100,
        },
        {
          do: 'link',
          label: 'Open SIU Triage Sentinel — Goals',
          href: '/agents/siu-triage-sentinel',
          delay: 600,
        },
      ],
      hold: [
        {
          do: 'say',
          text: 'It stays staged. Nothing has moved — no goal, no benchmark, no evaluation, and the finding shape on the desk is exactly what it was five minutes ago. The proposal keeps its evidence and sits there until somebody decides. That is what "the agent proposes" has to mean if it is going to mean anything.',
          delay: 900,
        },
      ],
    },
  },
];
